import { _, _any } from "i18n";
import { useMemo, useState } from "react";
import { Star, Check } from "lucide-react";
import { SearchInput } from "ui-kit-v2/src/search-input/search-input";
import { PlayerBadgesModalData, RatingBadgeResponse } from "../../../../gamePage/types";

interface PlayerBadgesTabProps {
	modalState: PlayerBadgesModalData;
	initBadgeIds?: number[];
	badges: RatingBadgeResponse[];
	setModalState: React.Dispatch<React.SetStateAction<PlayerBadgesModalData>>;
	maxBadgeCount?: number;
	lockInitBadges?: boolean;
}

function toHttpsAssetUrl(asset: string): string {
	if (asset.startsWith("http://")) {
		return `https://${asset.slice("http://".length)}`;
	}

	return asset;
}

export function PlayerBadgesTab({ initBadgeIds, modalState, setModalState, badges, maxBadgeCount, lockInitBadges = true }: PlayerBadgesTabProps) {
	const [searchQuery, setSearchQuery] = useState("");

	const filteredBadges = useMemo(() => {
		const normalizedQuery = searchQuery.trim().toLowerCase();
		if (normalizedQuery === "") {
			return badges;
		}

		return badges.filter((badge) => badge.name.toLowerCase().includes(normalizedQuery));
	}, [badges, searchQuery]);

	return (
		<div>
			<div className="playerRatingModal-ratingHeader">
				<span className="rating-title">
					{_any("gamePage.playerRating.modal.badge.title", {
						player: modalState.name ?? "",
					})}
				</span>
				<span className="rating-text">{_("gamePage.playerRating.modal.badge.text")}</span>
			</div>

			<div className="playerRatingModal-badgeSearch">
				<SearchInput
					value={searchQuery}
					onChange={setSearchQuery}
					placeholder={_("label.search")}
					isLoading={false}
					hasSubmitButton={false}
					className="w-full mx-0"
				/>
			</div>

			<div className="playerRatingModal-badges">
				{filteredBadges.map((badge) => {
					const selected = modalState.badgeIds.includes(badge.id);
					const isInit = initBadgeIds?.includes(badge.id) === true;
					const selectionLimitReached = maxBadgeCount !== undefined && modalState.badgeIds.length >= maxBadgeCount;

					return (
						<button
							type="button"
							key={badge.id}
							className={`badge-container ${selected ? "selected" : ""} ${isInit ? "initBadge" : ""}`}
							disabled={selected === false && selectionLimitReached}
							onClick={() =>
								setModalState((prev) => {
									const isSelected = prev.badgeIds.includes(badge.id);

									if (isSelected && isInit && lockInitBadges) {
										return prev;
									}

									if (isSelected === false && maxBadgeCount !== undefined && prev.badgeIds.length >= maxBadgeCount) {
										return prev;
									}

									const nextIds = isSelected
										? prev.badgeIds.filter((badgeId) => badgeId !== badge.id)
										: [...prev.badgeIds, badge.id];

									return {
										...prev,
										badgeIds: nextIds,
									};
								})
							}
						>
							<div className="badge-image-frame">
								<img className="badge-image" src={toHttpsAssetUrl(badge.asset)} alt={badge.name} />
							</div>
							<div className="badge-label">
								<Star size={16} />
								<div className="badge-label-text">{badge.name}</div>
								{selected && <Check size={16} />}
							</div>
						</button>
					);
				})}
			</div>
		</div>
	);
}
